// React, Router, Context and Services
import { AbilityContext } from "Can";
import { useContext, useEffect, useState } from "react";
import ordersService from "services/orders.service";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";

// MUI and Material Dashboard components
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";

// Page components and helper functions
import { convertDate } from "../components/helperFunctions";
import CreateOrder from "./components/create-order";
import EditOrder from "./components/edit-order";
import ConfirmDialog from "e4acomponents/ConfirmDialog"

export default function ManageOrders() {
    const [openCreateOrder, setOpenCreateOrder] = useState(false);
    const [editOrderId, setEditOrderId] = useState(null);
    const [openEditOrder, setOpenEditOrder] = useState(false);
    const [orders, setOrders] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false
    });

    const ability = useContext(AbilityContext);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const ordersResponse = await ordersService.getOrders();
                setOrders(ordersResponse.data.attributes);
            } catch (error) {
                console.error("Error fetching orders:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [])

    useEffect(() => {
        setTableData(getRows(orders));
    }, [orders])

    // Handlers for Create and Edit Order dialogs
    const handleOpenCreateOrder = () => setOpenCreateOrder(true);
    const handleCloseCreateOrder = async () => {
        try {
            setOpenCreateOrder(false)
            // Refresh orders list after creating a new order
            await refetchOrders();
        } catch (error) {
            console.error("Error refreshing orders after creation:", error);
        }
    };

    // Handlers for Edit Order dialog
    const handleOpenEditOrder = (id) => {
        setEditOrderId(id);
        setOpenEditOrder(true);
    };

    const handleCloseEditOrder = async () => {
        try {
            setOpenEditOrder(false);
            await refetchOrders();
        } catch (error) {
            console.error("Error refreshing orders after edit:", error);
        }
    };

    // Function to refresh dataTable
    const refetchOrders = async () => {
        setIsLoading(true);
        try {
            const ordersResponse = await ordersService.getOrders();
            setOrders(ordersResponse.data.attributes);
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Transforms order data into table rows
    const getRows = (info) => {
        return info.map((order) => ({
            orderNum: order.order_num,
            organization: order.organization.name,
            qty: order.qty,
            orgchangedAt: convertDate(order.org_changed_at),
            id: order._id,
        }));
    }

    // Defines the structure and content of the data table
    const dataTableData = {
        columns: [
            { Header: "Order #", accessor: "orderNum", type: "number" },
            { Header: "Organization", accessor: "organization" },
            { Header: "Quantity", accessor: "qty", type: "number" },
            { Header: "Last Changed", accessor: "orgchangedAt" },
            {
                Header: "Actions",
                accessor: "actions",
                disableSortBy: true,
                Cell: ({ row }) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            
                            {ability.can("delete", "orders") && (
                                <Tooltip title="Delete Order" placement="top">
                                    <IconButton
                                        color="error"
                                    >
                                        <DeleteTwoToneIcon color="error" size="small" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {ability.can("edit", "orders") && (
                                <Tooltip title="Edit Order" placement="top">
                                    <IconButton
                                        color="primary"
                                        onClick={() =>
                                            handleOpenEditOrder(row.original.id)}
                                    >
                                        <EditTwoToneIcon color="success" size="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    )
                }
            },
        ],
        rows: tableData
    };

    const handleOpenConfirmDialog = (orderId) => {
        // TODO
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            {/* Create Order Dialog */}
            <CreateOrder
                open={openCreateOrder}
                handleClose={handleCloseCreateOrder}
            />

            {/* Edit Order Dialog */}
            <EditOrder
                open={openEditOrder}
                handleClose={handleCloseEditOrder}
                orderId={editOrderId}
            />

            <Card>
                <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">

                    {/* Title */}
                    <MDTypography variant="h5" fontWeight="medium">
                        Manage Orders
                    </MDTypography>

                    {/* Add New Product Button */}
                    {ability.can("create", "orders") && (
                        <MDButton
                            variant="gradient"
                            color="info"
                            size="small"
                            type="submit"
                            onClick={handleOpenCreateOrder}
                        >
                            Create Order
                        </MDButton>
                    )}

                </MDBox>

                {/* Data Table */}
                <MDBox>
                    <DataTable
                        table={dataTableData}
                        canSearch={true}
                        isLoading={isLoading}
                        tableId="manage-orders-table"
                    />
                </MDBox>

            </Card>

            {/* Page's Footer */}
            <MDBox mt={6}>
                <Footer />
            </MDBox>
        </DashboardLayout>
    )
}